import { Injectable } from '@angular/core';
import { AssistantType, listAvailableModels$, Model, ModelType } from '@vendasta/protos/ai_assistants/v1alpha1';
import { Observable, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ModelWithReasoningSupport {
  id: string;
  name: string;
  supportedReasoningEfforts: string[];
}

/**
 * Service that fetches and caches available LLM models.
 * Uses shareReplay to ensure the API is called once per assistant type and the result is cached.
 */
@Injectable({
  providedIn: 'root',
})
export class AvailableModelsService {
  private readonly modelsByType = new Map<string, Observable<Model[]>>();
  private modelNameLookup$: Observable<Map<string, string>> | null = null;
  private modelReasoningLookup$: Observable<Map<string, string[]>> | null = null;

  /**
   * Gets the full list of available models.
   *
   * The backend marks which model is `recommended` per assistant type — the model an assistant of that type
   * runs when nothing pins one — so callers that use the flag must say whose models they are asking for.
   * The result is cached per type and shared across all subscribers.
   */
  getModels(assistantType?: AssistantType): Observable<Model[]> {
    const cacheKey = assistantType ?? '';
    let models$ = this.modelsByType.get(cacheKey);
    if (!models$) {
      models$ = listAvailableModels$({
        filters: { type: [ModelType.TYPE_LARGE_LANGUAGE_MODEL], vendor: [], assistantType },
      }).pipe(
        map((response) => response.models || []),
        shareReplay(1),
      );
      this.modelsByType.set(cacheKey, models$);
    }
    return models$;
  }

  /**
   * Gets a map of model ID to human-readable model name.
   * The result is cached and shared across all subscribers.
   */
  getModelNameLookup(): Observable<Map<string, string>> {
    if (!this.modelNameLookup$) {
      this.modelNameLookup$ = this.getModels().pipe(
        map((models) => {
          const modelNameLookup = new Map<string, string>();
          models.forEach((model) => {
            if (model.id && model.name) {
              modelNameLookup.set(model.id, model.name);
            }
          });
          return modelNameLookup;
        }),
        shareReplay(1),
      );
    }
    return this.modelNameLookup$;
  }

  /**
   * Gets a map of model ID to its supported reasoning efforts.
   * The result is cached and shared across all subscribers.
   */
  getModelReasoningLookup(): Observable<Map<string, string[]>> {
    if (!this.modelReasoningLookup$) {
      this.modelReasoningLookup$ = this.getModels().pipe(
        map((models) => {
          const reasoningLookup = new Map<string, string[]>();
          models.forEach((model) => {
            if (model.id) {
              reasoningLookup.set(model.id, model.supportedReasoningEfforts || []);
            }
          });
          return reasoningLookup;
        }),
        shareReplay(1),
      );
    }
    return this.modelReasoningLookup$;
  }
}
